import apiClient from './api'
import type { ApiResponse, NikaidoConnectionStatus, NikaidoTriggerContract, NikaidoWorkflow, NikaidoWorkflowRequirements } from '@/types'

class NikaidoService {
  async getStatus(): Promise<NikaidoConnectionStatus> {
    const response = await apiClient.get<ApiResponse<NikaidoConnectionStatus>>('/settings/nikaido')
    return response.data.data!
  }

  async provision(): Promise<NikaidoConnectionStatus> {
    const response = await apiClient.post<ApiResponse<NikaidoConnectionStatus>>('/settings/nikaido/provision')
    return response.data.data!
  }

  async connect(data: {
    token: string
    api_base_url?: string
    web_base_url?: string
  }): Promise<NikaidoConnectionStatus> {
    const response = await apiClient.post<ApiResponse<NikaidoConnectionStatus>>('/settings/nikaido/connect', data)
    return response.data.data!
  }

  async disconnect(): Promise<NikaidoConnectionStatus> {
    const response = await apiClient.delete<ApiResponse<NikaidoConnectionStatus>>('/settings/nikaido')
    return response.data.data!
  }

  async listWorkflows(): Promise<NikaidoWorkflow[]> {
    const response = await apiClient.get<ApiResponse<NikaidoWorkflow[]>>('/settings/nikaido/workflows')
    return response.data.data || []
  }

  async createWorkflow(name: string): Promise<NikaidoWorkflow> {
    const response = await apiClient.post<ApiResponse<NikaidoWorkflow>>('/settings/nikaido/workflows', { name })
    if (response.data.data) return response.data.data
    throw new Error('Failed to create workflow')
  }

  async getWorkflowEmbedUrl(workflowId: string): Promise<string> {
    const response = await apiClient.get<ApiResponse<{ url: string }>>(`/settings/nikaido/workflows/${workflowId}/embed-url`)
    if (response.data.data?.url) return response.data.data.url
    throw new Error('Failed to load workflow editor')
  }

  async getWorkflowRequirements(workflowId: string): Promise<NikaidoWorkflowRequirements> {
    const response = await apiClient.get<ApiResponse<NikaidoWorkflowRequirements>>(`/settings/nikaido/workflows/${workflowId}/requirements`)
    if (response.data.data) return response.data.data
    throw new Error('Failed to load workflow requirements')
  }

  async getWorkflowTriggerContract(workflowId: string): Promise<NikaidoTriggerContract> {
    const response = await apiClient.get<ApiResponse<NikaidoTriggerContract>>(`/settings/nikaido/workflows/${workflowId}/trigger-contract`)
    if (response.data.data) return response.data.data
    throw new Error('Failed to load workflow trigger contract')
  }

  async updateWorkflowSkillBindings(workflowId: string, skillBindings: Record<string, string>): Promise<NikaidoWorkflowRequirements> {
    const response = await apiClient.put<ApiResponse<NikaidoWorkflowRequirements>>(`/settings/nikaido/workflows/${workflowId}/skill-bindings`, { skillBindings })
    if (response.data.data) return response.data.data
    throw new Error('Failed to update workflow requirements')
  }
}

export const nikaidoService = new NikaidoService()
